import React from 'react';
// Api
import client from '../../../api/client';
// Constants
import {
  DELETE_ALL_SUBSCRIBERS_API,
  DELETE_SUBSCRIBER_BY_ID_API,
} from '../../../utils/constants/ApiRoutes';
import { CompanyName } from '../../../utils/constants/Constants';
// Icons
import { IoMdRefreshCircle } from 'react-icons/io';
// Utils
import LoadingSpinner from '../../utils/LoadingSpinner';
// Components
import MailingListSubscriberItem from './MailingListSubscriberItem';

function MailingListAdminSubscriberSection({
  mailingListSubscribers,
  setMailingListSubscribers,
  confirmAction,
  fetchMailingListSubscribers,
  isLoadingSubscriberList,
}) {

  const handleDelete = (id) => {
    confirmAction({
      header: 'Delete Subscriber',
      message: 'Are you sure you want to delete this subscriber?',
      action: () => {
        client
          .delete(`${DELETE_SUBSCRIBER_BY_ID_API}/${id}`, false)
          .then(() => {
            setMailingListSubscribers((prev) =>
              prev.filter((form) => form.id !== id)
            );
          })
          .catch((err) => {
            console.error('Failed to delete subscriber', err);
          });
      },
    });
  };

  const handleDeleteAll = () => {
    confirmAction({
      header: 'Delete All Subscribers',
      message:
        'Are you sure you want to delete all subscribers? This cannot be undone.',
      action: () => {
        client
          .delete(DELETE_ALL_SUBSCRIBERS_API, false)
          .then(() => {
            setMailingListSubscribers([]);
          })
          .catch((err) => {
            console.error('Failed to delete all subscribers', err);
          });
      },
    });
  };

  return (
    <section className='grid w-full bg-colour4'>
      <div className='grid grid-rows-reg w-full border-solid border-colour8 border-4'>
        {/* Header */}
        <div>
          <section
            className='grid w-full bg-colour5 px-2 py-2'
            aria-label='Callback form admin header'
          >
            <div className='grid grid-flow-col items-center justify-between'>
              <h1 className='text-sm md:text-xl' id='subscriber-list-heading'>
                Subscribers ({mailingListSubscribers.length})
              </h1>
              {mailingListSubscribers.length > 0 && (
                <aside className='grid grid-flow-col gap-x-2'>
                  <div>
                    <button
                      onClick={fetchMailingListSubscribers}
                      className='flex items-center h-full hover:brightness-125 text-2xl text-colour1 active:scale-95'
                      aria-describedby='delete-all-description'
                      title='Refresh subscriber list'
                    >
                      <IoMdRefreshCircle />
                    </button>
                    <span
                      id='refresh-subscriber-list-description'
                      className='sr-only'
                    >
                      Refresh subscriber list
                    </span>
                  </div>
                  <div>
                    <button
                      onClick={handleDeleteAll}
                      className='bg-red-500 hover:bg-red-700 text-colour1 font-medium py-1 px-4 text-xs h-full rounded'
                      aria-label='Delete all subscribers permanently'
                    >
                      Delete All
                    </button>
                    <span id='delete-all-description' className='sr-only'>
                      Deletes all stored mailingList subscribers permanently
                    </span>
                  </div>
                </aside>
              )}
            </div>
          </section>
        </div>

        <div
          className={`grid gap-y-2 ${
            mailingListSubscribers.length > 0 ? 'grid' : ''
          }`}
        >
          {/* Email Copy Section */}
          {mailingListSubscribers.length > 0 && (
            <section className='grid w-full px-2'>
              <div className='grid grid-rows-reg w-full bg-colour1 px-2 py-2 h-full rounded shadow'>
                <h2 className='font-semibold text-sm text-colour2'>
                  All Emails (comma-separated):
                </h2>
                <div className='text-xxs text-black break-words'>
                  {mailingListSubscribers.map((s) => s.email).join(', ')}
                </div>
              </div>
            </section>
          )}

          {/* Subscribers */}
          <section
            className='grid w-full px-2 h-full'
            aria-label={`List of submitted subscribers for ${CompanyName}`}
          >
            {isLoadingSubscriberList ? (
              <section className='grid h-full items-center justify-center'>
                <LoadingSpinner lg={true} />
              </section>
            ) : (
              <>
                {mailingListSubscribers.length === 0 ? (
                  <section
                    className='grid items-center justify-center h-full w-full'
                    role='status'
                    aria-live='polite'
                  >
                    <p>No subscribers found.</p>
                  </section>
                ) : (
                  <ul className='grid gap-y-1 lg:grid-cols-3 gap-x-2 h-fit py-2'>
                    {mailingListSubscribers.map((subscriber, index) => (
                      <li key={index}>
                        <MailingListSubscriberItem
                          id={index}
                          subscriber={subscriber}
                          handleDelete={handleDelete}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </section>
  );
}

export default MailingListAdminSubscriberSection;
